import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { trackConversion } from "@/lib/gtag";
import { getPost, SORTED_POSTS, type BlogPost } from "@/lib/blog/posts";
import { CalendarDays, Clock, Phone, ArrowRight } from "lucide-react";

const BASE = "https://sepremiumcabs.com";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    const post = loaderData as BlogPost | undefined;
    if (!post) return {};
    const url = `${BASE}/blog/${post.slug}`;
    return {
      meta: [
        { title: post.metaTitle },
        { name: "description", content: post.metaDescription },
        { property: "og:title", content: post.metaTitle },
        { property: "og:description", content: post.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: `${BASE}/og-image.jpg` },
        { name: "twitter:image", content: `${BASE}/og-image.jpg` },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.metaDescription,
            datePublished: post.date,
            dateModified: post.date,
            mainEntityOfPage: url,
            image: `${BASE}/og-image.jpg`,
            author: { "@type": "Organization", name: "SE Premium Cabs", url: BASE },
            publisher: { "@type": "Organization", name: "SE Premium Cabs", url: BASE },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
              { "@type": "ListItem", position: 2, name: "Guides", item: `${BASE}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const post = Route.useLoaderData() as BlogPost;
  const others = SORTED_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="mx-auto max-w-[820px] px-6 py-10">
        <nav className="text-[11px] tracking-widest text-muted-foreground">
          <Link to="/" className="hover:text-primary">
            HOME
          </Link>{" "}
          /{" "}
          <Link to="/blog" className="hover:text-primary">
            GUIDES
          </Link>
        </nav>

        <h1 className="mt-4 font-serif text-3xl font-bold leading-tight tracking-tight md:text-4xl">
          {post.title}
        </h1>
        <div className="mt-4 flex items-center gap-4 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <CalendarDays className="h-3 w-3" />
            {new Date(post.date).toLocaleDateString("en-AU", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {post.readMinutes} min read
          </span>
        </div>

        <div className="mt-8 space-y-5">
          {post.blocks.map((b, i) => {
            if (b.type === "h2")
              return (
                <h2 key={i} className="pt-4 font-serif text-2xl font-bold tracking-tight">
                  {b.text}
                </h2>
              );
            if (b.type === "h3")
              return (
                <h3 key={i} className="pt-2 text-base font-bold tracking-wide">
                  {b.text}
                </h3>
              );
            if (b.type === "p")
              return (
                <p key={i} className="text-sm leading-7 text-muted-foreground">
                  {b.text}
                </p>
              );
            if (b.type === "ul")
              return (
                <ul key={i} className="space-y-2 pl-5">
                  {b.items.map((it) => (
                    <li key={it} className="list-disc text-sm leading-7 text-muted-foreground">
                      {it}
                    </li>
                  ))}
                </ul>
              );
            if (b.type === "table")
              return (
                <div key={i} className="overflow-x-auto rounded-lg border border-border/60">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-border/60 text-[10px] font-bold tracking-widest text-muted-foreground">
                        {b.head.map((h) => (
                          <th key={h} className="px-4 py-3">
                            {h.toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {b.rows.map((r) => (
                        <tr key={r[0]} className="border-b border-border/40 last:border-0">
                          {r.map((c, ci) => (
                            <td
                              key={ci}
                              className={
                                ci === 0
                                  ? "px-4 py-3 font-semibold"
                                  : "px-4 py-3 text-muted-foreground"
                              }
                            >
                              {c}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            return (
              <div
                key={i}
                className="rounded-xl border border-primary/40 bg-secondary/40 p-6 text-center"
              >
                <p className="font-serif text-lg font-bold">{b.text}</p>
                <div className="mt-4 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/melbourne-airport-taxi"
                    hash="book"
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-xs font-bold tracking-widest text-primary-foreground"
                  >
                    BOOK ONLINE <ArrowRight className="h-3 w-3" />
                  </Link>
                  <a
                    href="tel:0493100265"
                    onClick={() => trackConversion("phoneCall")}
                    className="inline-flex items-center gap-2 rounded-md border border-primary px-5 py-2.5 text-xs font-bold tracking-widest text-primary"
                  >
                    <Phone className="h-3 w-3" /> CALL 0493 100 265
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {others.length > 0 && (
          <section className="mt-14 border-t border-border/60 pt-8">
            <h2 className="font-serif text-xl font-bold tracking-tight">Keep reading</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="rounded-lg border border-border/60 bg-card p-5 hover:border-primary/60"
                >
                  <h3 className="text-sm font-bold leading-snug">{p.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      <div className="h-16 lg:hidden" />
      <StickyMobileBar />
    </div>
  );
}
